import { motion, useInView } from "framer-motion"
import { Link, graphql } from "gatsby"
import parse from "html-react-parser"
import React, { useEffect, useRef, useState } from "react"
import Modal from "react-modal"
import ReactPlayer from "react-player"
import ImageOpt from "./ImageOpt"

const Gallery = ({ module }) => {
  const ref = useRef(null)
  const [hasPlayed, setHasPlayed] = useState(false)
  const isInView = useInView(ref)

  const sectionShow = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut",
        delayChildren: 0.5,
        staggerChildren: 0.2,
      },
    },
  }
  const animFade = {
    hidden: { opacity: 0, y: 20 },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeInOut",
        delayChildren: 0.5,
        staggerChildren: 0.2,
      },
    },
  }

  useEffect(() => {
    if (isInView && !hasPlayed) {
      setHasPlayed(true)
    }
  }, [isInView, hasPlayed, setHasPlayed])

  const customStyles = {
    content: {
      top: "50%",
      left: "50%",
      right: "auto",
      bottom: "auto",
      marginRight: "-50%",
      transform: "translate(-50%, -50%)",
      padding: "0",
      border: "0",
      borderRadius: "0",
      width: "100%",
      height: "100%",
      zIndex: "99999999",
      backgroundColor: "black",
    },
    overlay: {
      zIndex: "99999999",
    },
  }

  const [modalIsOpen, setModalIsOpen] = useState(false)
  const [currentItem, setCurrentItem] = useState(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const [displayedImages, setDisplayedImages] = useState(10)

  const openModal = (item, rowIndex, itemIndex) => {
    const indexInArray = rowIndex * 2 + itemIndex // Adjust for your grid layout
    setCurrentItem(item)
    setActiveIndex(indexInArray)
    setModalIsOpen(true)
    document.body.style.overflow = "hidden"
  }

  const closeModal = () => {
    setCurrentItem(null)
    setModalIsOpen(false)
    document.body.style.overflow = ""
  }

  const nextItem = () => {
    const nextIndex = (activeIndex + 1) % module.images.length
    setCurrentItem(module.images[nextIndex])
    setActiveIndex(nextIndex)
  }

  const prevItem = () => {
    const prevIndex =
      (activeIndex - 1 + module.images.length) % module.images.length
    setCurrentItem(module.images[prevIndex])
    setActiveIndex(prevIndex)
  }

  const alternateChunkArray = (arr, chunkSize1, chunkSize2) => {
    const result = []
    let i = 0
    while (i < arr.length) {
      if (result.length % 2 === 0) {
        result.push(arr.slice(i, i + chunkSize1))
        i += chunkSize1
      } else {
        result.push(arr.slice(i, i + chunkSize2))
        i += chunkSize2
      }
    }
    return result
  }

  const rows = alternateChunkArray(
    module?.images.slice(0, displayedImages) || [],
    2,
    3
  )

  const handleLoadMore = () => {
    const newDisplayedImages = displayedImages + 10
    setDisplayedImages(newDisplayedImages)
    let newIndex = activeIndex
    if (activeIndex >= newDisplayedImages) {
      newIndex = newDisplayedImages - 1
    }
    setActiveIndex(newIndex + 2)
  }

  return (
    <React.Fragment>
      <motion.section
        initial="hidden"
        animate={isInView || hasPlayed ? "show" : "hidden"}
        variants={sectionShow}
        className={`general-content py-60 mdscreen2:py-45 bg-pattern-2 pattern-square ${
          module.extraClass ? ` ${module.extraClass}` : ""
        }`}
        id={module.extraId}
      >
        <motion.div
          ref={ref}
          initial="hidden"
          animate={isInView || hasPlayed ? "show" : "hidden"}
          variants={sectionShow}
          className="container-fluid-sm"
        >
          <div className="flex flex-col items-center gap-25 mdscreen2:gap-20 max-w-[680px] mx-auto text-center">
            <motion.div variants={animFade} className="icon">
              <img
                src="../../images/green-ring.svg"
                alt="ring-icon"
                height={40}
                width={40}
                loading="lazy"
              />
            </motion.div>
            {module?.heading && (
              <motion.div variants={animFade} className="title title-green">
                <h3 className="uppercase tracking-0.04em font-500">
                  {module.heading}
                </h3>
              </motion.div>
            )}
            {module?.description && (
              <motion.div variants={animFade} className="content">
                <p>{parse(module.description)}</p>
              </motion.div>
            )}
            <motion.div variants={animFade} className="custom-btn">
              <Link to={"/gallery"} className="btn-icon btn-underline-left">
                <img
                  src="../../images/simple-arrow.svg"
                  alt="download-icon"
                  height={14}
                  width={14}
                />
                <span>Go back</span>
              </Link>
            </motion.div>
          </div>
        </motion.div>
      </motion.section>

      <motion.section
        initial="hidden"
        animate={isInView || hasPlayed ? "show" : "hidden"}
        variants={sectionShow}
        className={`gallery-listing py-80 mdscreen2:py-50 !pt-0 ${
          module.extraClass ? ` ${module.extraClass}` : ""
        }`}
        id={module.extraId}
      >
        <motion.div
          ref={ref}
          initial="hidden"
          animate={isInView || hasPlayed ? "show" : "hidden"}
          variants={sectionShow}
          className="container-fluid-sm"
        >
          <div className="flex flex-wrap lg:-mx-15 lgscreen:mx-0 lgscreen:mt-20 lg:gap-y-[45px] md:gap-y-[15px] gap-y-[30px]">
            {rows.map((row, rowIndex) => (
              <div
                key={rowIndex}
                className={`w-full flex flex-wrap gap-y-[30px] lgscreen:gap-y-[25px] ${
                  rowIndex % 2 === 0 ? "even-row" : "odd-row"
                }`}
              >
                {row.map((item, itemIndex) => (
                  <motion.div
                    variants={animFade}
                    key={itemIndex}
                    className={`item_wrapper w-full lg:px-15 md:px-5 px-0 relative`}
                  >
                    <div
                      className="img relative"
                      onClick={() => openModal(item, rowIndex, itemIndex)}
                    >
                      <img
                        src={item.image?.mediaItemUrl}
                        alt={item.alt || "media-item"}
                        width={100}
                        height={100}
                        loading="lazy"
                        className="cursor-pointer"
                      />

                      {item.mediaType === "video" && (
                        <div className="absolute top-50per translate-y-minus_50 left-50per translate-x-minus_50 h-[68px] w-[68px] mdscreen2:h-[50px] mdscreen2:w-[50px] flex justify-center items-center border-[5px] mdscreen2:border-[3px] border-white-100 rounded-100 opacity-50 z-10 cursor-pointer">
                          <img
                            src="../../images/play-icon-2.svg"
                            alt="play-icon"
                            height={25}
                            width={20}
                            loading="lazy"
                            className="!h-[50%] !w-[50%] !object-contain !object-center !static translate-x-[3px]"
                          />
                        </div>
                      )}
                    </div>
                    <div className="absolute left-0 bottom-0 w-full pb-20 px-40 mdscreen2:px-20 z-9 pointer-events-none">
                      {item.imageCaption && (
                        <div className="title-white">
                          <h3 className="text-14 leading-22 italic">
                            {item.imageCaption}
                          </h3>
                        </div>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            ))}
          </div>

          {displayedImages < (module?.images || []).length && (
            <motion.div
              variants={animFade}
              className="btn-custom mt-90 xlscreen:mt-70 mdscreen:mt-60 flex justify-center items-center"
            >
              <button onClick={handleLoadMore} className="btn btn-grey">
                Load more
              </button>
            </motion.div>
          )}
        </motion.div>
      </motion.section>

      {/* Modal */}
      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        style={customStyles}
        contentLabel="Media Modal"
      >
        <div className="flex smscreen2:justify-center smscreen2:flex-col h-full w-full px-100 py-60 smscreen2:px-20 smscreen2:py-20 overflow-y-auto">
          <button
            onClick={closeModal}
            className="absolute right-40 top-60 mdscreen:right-35 smscreen2:top-30 bg-white-100 flex items-center justify-center w-[40px] h-[40px] rounded-100 modal-close-btn z-99"
          >
            <img
              src="../../images/close-icon.svg"
              alt="Close"
              width={20}
              height={20}
            />
          </button>
          {currentItem?.mediaType === "video" ? (
            <ReactPlayer
              url={currentItem.videoLink?.url}
              width="100%"
              height="100%"
              controls
              playing
              loop
            />
          ) : (
            <img
              src={currentItem?.image?.mediaItemUrl}
              alt={currentItem?.image?.altText || "Image"}
              // style={{ maxWidth: "100%", height: "auto" }}
              className="sm:m-auto w-full object-contain sm:h-full object-center smscreen2:object-contain"
            />
          )}
          <div className="modal-controls smscreen2:flex smscreen2:gap-20 smscreen2:pt-25 smscreen2:justify-center">
            <button
              onClick={prevItem}
              className="prev-button smscreen2:static absolute top-50per left-35 flex justify-center items-center border-2 border-white-100 rounded-100 h-[35px] w-[35px]"
            >
              <img
                src="../../images/swiper-arrowPrev-white.svg"
                alt="Close"
                width={20}
                height={20}
              />
            </button>
            <button
              onClick={nextItem}
              className="next-button smscreen2:static absolute top-50per right-35 flex justify-center items-center border-2 border-white-100 rounded-100 h-[35px] w-[35px]"
            >
              <img
                src="../../images/swiper-arrowNext-white.svg"
                alt="Close"
                width={20}
                height={20}
              />
            </button>
          </div>
          {/* <div className="modal-footer">
          Current Image Index: {activeIndex + 1} of {module.images.length}
        </div> */}
        </div>
      </Modal>
    </React.Fragment>
  )
}

export default Gallery

export const PageGalleryFragment = graphql`
  fragment PageGalleryFragment on WpPage_Pagecontent_PageContent_Gallery {
    extraClass
    extraId
    hideSection
    description
    heading
    images {
      image {
        altText
        mediaItemUrl
        title
      }
      imageCaption
      mediaType
      videoLink {
        target
        title
        url
      }
    }
  }
`
